import { useState, useMemo, useRef, useEffect } from "react";
import { getCountries } from 'react-phone-number-input/input';
import { ChevronDown, X, Search } from 'lucide-react';
import { useTranslation } from "react-i18next";

interface CountryFilterProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
}

export default function CountryFilter({
    value,
    onChange,
    placeholder = "All Countries",
    className = ""
}: CountryFilterProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const { i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    // Generate country list
    // Memoize this to avoid expensive recalculations
    const countries = useMemo(() => {
        try {
            const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
            return getCountries().map((country) => ({
                code: country,
                name: regionNames.of(country) || country,
            })).sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
            console.error("Failed to generate country list:", e);
            return [];
        }
    }, []);

    const filteredCountries = useMemo(() => {
        if (!searchQuery) return countries;
        const lowerQuery = searchQuery.toLowerCase();
        return countries.filter(c =>
            c.name.toLowerCase().includes(lowerQuery) ||
            c.code.toLowerCase().includes(lowerQuery)
        );
    }, [countries, searchQuery]);

    const selectedCountry = useMemo(() => {
        return countries.find(c => c.name === value);
    }, [countries, value]);

    // Handle click outside to close dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full px-4 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} text-slate-700 border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between hover:bg-slate-50 transition-colors`}
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {selectedCountry ? (
                        <>
                            <img
                                src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
                                alt={selectedCountry.name}
                                className="w-5 h-auto object-cover rounded-sm border border-slate-100 flex-shrink-0"
                            />
                            <span className="truncate">{selectedCountry.name}</span>
                        </>
                    ) : (
                        <span className="text-slate-500 truncate">{value || placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {value && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange("");
                            }}
                            className="p-0.5 hover:bg-slate-200 rounded-full cursor-pointer text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <X className="w-3.5 h-3.5" />
                        </div>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 flex flex-col min-w-[200px]">
                    <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10 rounded-t-lg">
                        <div className="relative">
                            <Search className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-3.5 h-3.5`} />
                            <input
                                type="text"
                                placeholder="Search..."
                                className={`w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-blue-500 text-slate-700 ${isRTL ? 'text-right' : 'text-left'}`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="overflow-auto flex-1">
                        <button
                            type="button"
                            onClick={() => {
                                onChange("");
                                setIsOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-sm ${isRTL ? 'text-right' : 'text-left'} text-slate-600 hover:bg-slate-50 border-b border-slate-50`}
                        >
                            {placeholder}
                        </button>
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map(({ code, name }) => (
                                <button
                                    key={code}
                                    type="button"
                                    onClick={() => {
                                        onChange(name);
                                        setIsOpen(false);
                                    }}
                                    className={`w-full px-4 py-2.5 text-sm ${isRTL ? 'text-right' : 'text-left'} hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0 ${value === name ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}
                                >
                                    <img
                                        src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
                                        alt={name}
                                        className="w-5 h-auto object-cover rounded-sm border border-slate-100 flex-shrink-0"
                                    />
                                    <span className="truncate">{name}</span>
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-3 text-sm text-slate-400 text-center">
                                No countries found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
